import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createIdea, hasUserApprovedIdea } from '../services/ideas';
import toast from 'react-hot-toast';

const SubmitIdeaPage = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    platform: 'Kalshi',
    marketUrl: '',
    position: 'Yes',
    entryPrice: '',
    targetPrice: '',
    stopLoss: '',
    reasoning: '',
    marketExpirationDate: '',
    tags: [],
    riskLevel: 'Medium',
  });

  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState('');

  const platforms = ['Kalshi', 'Polymarket', 'Manifold', 'PredictIt', 'Other'];
  const positions = ['Yes', 'No', 'Long', 'Short'];
  const riskLevels = ['Low', 'Medium', 'High'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 5) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length < 10) {
      newErrors.title = 'Title must be at least 10 characters';
    } else if (formData.title.length > 150) {
      newErrors.title = 'Title must be less than 150 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    }

    if (!formData.entryPrice || isNaN(formData.entryPrice) || parseFloat(formData.entryPrice) <= 0) {
      newErrors.entryPrice = 'Valid entry price is required';
    }

    if (!formData.targetPrice || isNaN(formData.targetPrice) || parseFloat(formData.targetPrice) <= 0) {
      newErrors.targetPrice = 'Valid target price is required';
    }

    if (!formData.reasoning.trim()) {
      newErrors.reasoning = 'Investment reasoning is required';
    } else if (formData.reasoning.length < 100) {
      newErrors.reasoning = 'Reasoning must be at least 100 characters';
    }

    if (!formData.marketExpirationDate) {
      newErrors.marketExpirationDate = 'Market expiration date is required';
    } else {
      const selectedDate = new Date(formData.marketExpirationDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      if (selectedDate <= today) {
        newErrors.marketExpirationDate = 'Expiration date must be in the future';
      }
    }

    if (!formData.marketUrl.trim()) {
      newErrors.marketUrl = 'Market URL is required';
    } else if (!isValidUrl(formData.marketUrl)) {
      newErrors.marketUrl = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Check if user has any approved ideas
      // If they don't, this is their first idea and requires approval
      const hasApprovedIdea = await hasUserApprovedIdea(currentUser.uid);
      const requiresApproval = !hasApprovedIdea;
      
      const ideaData = {
        ...formData,
        entryPrice: parseFloat(formData.entryPrice),
        targetPrice: parseFloat(formData.targetPrice),
        stopLoss: formData.stopLoss ? parseFloat(formData.stopLoss) : null,
        authorId: currentUser.uid,
        authorName: userProfile?.displayName || currentUser.displayName || 'Anonymous',
        authorEmail: currentUser.email,
        status: requiresApproval ? 'pending' : 'active', // Pending if first idea, active otherwise
        moderationStatus: requiresApproval ? 'pending' : 'approved', // Requires moderation if first idea
        isOnboardingIdea: requiresApproval,
        outcome: null, // will be set when resolved
        roi: null, // will be calculated when resolved
        votes: 0,
        comments: 0,
        views: 0,
      };

      await createIdea(ideaData);
      
      if (requiresApproval) {
        toast.success('🎉 Idea submitted! It will be reviewed by our team for approval.');
      } else {
        toast.success('🎉 Idea submitted successfully!');
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting idea:', error);
      toast.error(error.message || 'Failed to submit idea. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b border-gray-200 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-32 py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-primary-600 mr-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-primary-600">Submit New Idea</h1>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-gray-600 hover:text-primary-600"
            >
              Cancel
            </button>
          </div>
        </div>
      </nav>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
          {/* Title */}
          <div className="mb-6">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Idea Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., 2024 Presidential Election - Trump Victory"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
            <p className="mt-1 text-sm text-gray-500">{formData.title.length}/150 characters</p>
          </div>

          {/* Platform and Position Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-2">
                Platform <span className="text-red-500">*</span>
              </label>
              <select
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {platforms.map(platform => (
                  <option key={platform} value={platform}>{platform}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                Position <span className="text-red-500">*</span>
              </label>
              <select
                id="position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {positions.map(position => (
                  <option key={position} value={position}>{position}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Market URL */}
          <div className="mb-6">
            <label htmlFor="marketUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Market URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              id="marketUrl"
              name="marketUrl"
              value={formData.marketUrl}
              onChange={handleChange}
              placeholder="https://kalshi.com/markets/..."
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.marketUrl ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.marketUrl && <p className="mt-1 text-sm text-red-500">{errors.marketUrl}</p>}
            <p className="mt-1 text-sm text-gray-500">Required for verification when closing positions</p>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Provide a clear and concise overview of the prediction market opportunity..."
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
            <p className="mt-1 text-sm text-gray-500">Minimum 50 characters</p>
          </div>

          {/* Pricing Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label htmlFor="entryPrice" className="block text-sm font-medium text-gray-700 mb-2">
                Entry Price (¢) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="entryPrice"
                name="entryPrice"
                value={formData.entryPrice}
                onChange={handleChange}
                placeholder="45"
                min="0"
                max="100"
                step="0.01"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.entryPrice ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.entryPrice && <p className="mt-1 text-sm text-red-500">{errors.entryPrice}</p>}
            </div>

            <div>
              <label htmlFor="targetPrice" className="block text-sm font-medium text-gray-700 mb-2">
                Target Price (¢) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="targetPrice"
                name="targetPrice"
                value={formData.targetPrice}
                onChange={handleChange}
                placeholder="75"
                min="0"
                max="100"
                step="0.01"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.targetPrice ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.targetPrice && <p className="mt-1 text-sm text-red-500">{errors.targetPrice}</p>}
            </div>

            <div>
              <label htmlFor="stopLoss" className="block text-sm font-medium text-gray-700 mb-2">
                Stop Loss (¢) (Optional)
              </label>
              <input
                type="number"
                id="stopLoss"
                name="stopLoss"
                value={formData.stopLoss}
                onChange={handleChange}
                placeholder="30"
                min="0"
                max="100"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Market Expiration Date and Risk Level */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="marketExpirationDate" className="block text-sm font-medium text-gray-700 mb-2">
                Expected Market Expiration Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="marketExpirationDate"
                name="marketExpirationDate"
                value={formData.marketExpirationDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]} // Set minimum date to today
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.marketExpirationDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.marketExpirationDate && <p className="mt-1 text-sm text-red-500">{errors.marketExpirationDate}</p>}
            </div>

            <div>
              <label htmlFor="riskLevel" className="block text-sm font-medium text-gray-700 mb-2">
                Risk Level <span className="text-red-500">*</span>
              </label>
              <select
                id="riskLevel"
                name="riskLevel"
                value={formData.riskLevel}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {riskLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Reasoning */}
          <div className="mb-6">
            <label htmlFor="reasoning" className="block text-sm font-medium text-gray-700 mb-2">
              Investment Reasoning <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reasoning"
              name="reasoning"
              value={formData.reasoning}
              onChange={handleChange}
              rows={6}
              placeholder="Explain your analysis, key factors, catalysts, and why you believe this prediction is valuable..."
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.reasoning ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.reasoning && <p className="mt-1 text-sm text-red-500">{errors.reasoning}</p>}
            <p className="mt-1 text-sm text-gray-500">Minimum 100 characters - Be thorough and analytical</p>
          </div>

          {/* Tags */}
          <div className="mb-8">
            <label htmlFor="tagInput" className="block text-sm font-medium text-gray-700 mb-2">
              Tags (Optional - Max 5)
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                id="tagInput"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                placeholder="e.g., politics, sports, crypto"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={formData.tags.length >= 5}
              />
              <button
                type="button"
                onClick={handleAddTag}
                disabled={formData.tags.length >= 5}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-primary-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Submitting...' : 'Submit Idea'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitIdeaPage;

